import React from 'react';
import cx from 'classnames';
import { forTemplate } from 'app/common/lib/util/i18n';
import { Spinner } from 'app/src/components/Spinner';
import {
  useSelectCoverQuery,
  useUpdateCardCoverMutation,
} from '@trello/graphql/generated';
import styles from './SearchResultImageButton.less';
import { track } from '@trello/analytics';
import { unsplashClient } from '@trello/unsplash';
import { useFeatureFlag } from '@trello/feature-flag-client';
import {
  Analytics,
  Action,
  Source,
  ActionSubject,
} from '@trello/atlassian-analytics';

const format = forTemplate('card_cover_chooser');
const PhotoAttributionComponent = require('app/scripts/views/board-create/photo-attribution-component');

export interface SearchResultImageButtonProps {
  cardId: string;
  index: number;
  urls: { thumb: string; raw: string };
  user: { name: string; links: { html: string } };
  links: { download_location: string };
  onSetCover: () => void;
  onSetCoverFailed: () => void;
  buttonClassName?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

export const SearchResultImageButton: React.FunctionComponent<SearchResultImageButtonProps> = ({
  cardId,
  index,
  urls,
  links,
  user,
  onSetCover,
  onSetCoverFailed,
  buttonClassName,
  onClick,
}) => {
  const colorAndFullCoversEnabled = useFeatureFlag('remarkable.rmk-239', false);
  const { data } = useSelectCoverQuery({
    variables: { cardId },
    fetchPolicy: 'cache-only',
  });
  const [updateCardCover, { loading }] = useUpdateCardCoverMutation({
    onError: onSetCoverFailed,
    onCompleted: () => {
      onSetCover();
      track('Card Cover Chooser', 'Choose Unsplash Search Result');
    },
  });

  const card = data?.card;
  const containers = {
    organization: {
      id: card?.board.idOrganization,
    },
    board: {
      id: card?.board.id,
    },
    card: {
      id: card?.id,
    },
  };

  const setCover = async (cover: { url: string }) => {
    await updateCardCover({
      variables: {
        cardId,
        cover,
      },
    });

    Analytics.sendTrackEvent({
      action: Action.updated,
      actionSubject: ActionSubject.cover,
      source: Source.cardCoverInlineDialog,
      attributes: {
        type: 'unsplash',
        value: cover.url,
      },
      containers,
    });

    // Send Unsplash an analytics event
    unsplashClient.trackDownload({ links });
  };

  const coverButtonOverlay = () => {
    if (loading) {
      return (
        <div className={styles.upload}>
          <Spinner
            wrapperClassName={cx(styles.spinnerWrapper, {
              [styles.newFlow]: colorAndFullCoversEnabled,
            })}
            inline
            modLeft
            small
            text={format('uploading-ellipsis')}
          />
        </div>
      );
    } else {
      return (
        <PhotoAttributionComponent
          user={user}
          size={colorAndFullCoversEnabled ? 'small' : undefined}
        />
      );
    }
  };

  return (
    <div key={index} className={styles.backgroundGridItem}>
      <button
        className={cx('button', styles.coverTilesItem, buttonClassName)}
        style={{
          backgroundImage: `url('${urls.thumb}')`,
        }}
        onClick={(e) => {
          if (onClick) {
            onClick(e);
          }

          if (!e.isDefaultPrevented()) {
            setCover({
              url: unsplashClient.appendImageParameters(urls.raw),
            });
          }
        }}
      >
        {coverButtonOverlay()}
      </button>
    </div>
  );
};
